import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Circle, Square, Play, RotateCcw, Save, Upload, Loader2, Volume2, Disc3 } from "lucide-react";
import { api, APP_ORIGIN } from "@/lib/api";

const MARKER_DEFS = [
  ["talk_over_point", "Talk Over"], ["intro_start", "Intro"], ["vocal_start", "Vocal Start"],
  ["hook_point", "Hook"], ["outro_start", "Outro"], ["end", "End"],
];
const BED_LEAD = 4, BED_TAIL = 4;

function encodeWav(buffer) {
  const ch = Math.min(buffer.numberOfChannels, 2), sr = buffer.sampleRate, len = buffer.length;
  const bytes = 44 + len * ch * 2;
  const ab = new ArrayBuffer(bytes), v = new DataView(ab);
  const ws = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  ws(0, "RIFF"); v.setUint32(4, bytes - 8, true); ws(8, "WAVEfmt ");
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, ch, true);
  v.setUint32(24, sr, true); v.setUint32(28, sr * ch * 2, true); v.setUint16(32, ch * 2, true);
  v.setUint16(34, 16, true); ws(36, "data"); v.setUint32(40, len * ch * 2, true);
  let o = 44;
  for (let i = 0; i < len; i++) for (let c = 0; c < ch; c++) {
    const s = Math.max(-1, Math.min(1, buffer.getChannelData(c)[i]));
    v.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true); o += 2;
  }
  return new Blob([ab], { type: "audio/wav" });
}

function trimSilence(ctx, buf, thresh = 0.02) {
  const d = buf.getChannelData(0);
  let s = 0, e = d.length - 1;
  while (s < e && Math.abs(d[s]) < thresh) s++;
  while (e > s && Math.abs(d[e]) < thresh) e--;
  s = Math.max(0, s - buf.sampleRate * 0.1); e = Math.min(d.length, e + buf.sampleRate * 0.15);
  if (e - s < buf.sampleRate * 0.2) return buf;
  const out = ctx.createBuffer(buf.numberOfChannels, Math.floor(e - s), buf.sampleRate);
  for (let c = 0; c < buf.numberOfChannels; c++) out.getChannelData(c).set(buf.getChannelData(c).subarray(s, e));
  return out;
}

const peaksFor = (buf) => {
  const n = Math.max(24, Math.min(140, Math.round(buf.duration * 6)));
  const d = buf.getChannelData(0), step = Math.floor(d.length / n) || 1;
  return Array.from({ length: n }, (_, i) => {
    let m = 0;
    for (let j = i * step; j < Math.min((i + 1) * step, d.length); j += 20) m = Math.max(m, Math.abs(d[j]));
    return Math.max(0.06, Math.min(1, m * 1.6));
  });
};

export default function VoiceTrackRecorder({ vt, onSaved }) {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [state, setState] = useState("idle"); // idle | recording | ready | saving
  const [secs, setSecs] = useState(0);
  const [level, setLevel] = useState(0);
  const [peaks, setPeaks] = useState(null);
  const [duration, setDuration] = useState(0);
  const [markers, setMarkers] = useState({});
  const [duck, setDuck] = useState({ on: true, amount: -14, attack: 80, release: 400, music: 0.8, voice: 1.0 });
  const [beds, setBeds] = useState([]);
  const [bedSel, setBedSel] = useState({ lead: "synth", tail: "synth" });
  const [bedUp, setBedUp] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [title, setTitle] = useState("");
  const [loadingExisting, setLoadingExisting] = useState(false);
  const bufRef = useRef(null);
  const bedBufsRef = useRef({});
  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const meterRef = useRef(null);
  const previewCtxRef = useRef(null);
  const waveRef = useRef(null);
  const dragRef = useRef(null);

  const timeline = BED_LEAD + duration + BED_TAIL;

  useEffect(() => {
    if (vt) {
      setMarkers(vt.markers || {});
      setTitle(vt.title || "");
    }
  }, [vt?.vt_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { clearInterval(timerRef.current); cancelAnimationFrame(meterRef.current); stopPreview(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/production/voicetrack/beds").then((r) => setBeds(r.data.items || [])).catch(() => {});
  }, []);

  const listMics = async (stream) => {
    const devs = await navigator.mediaDevices.enumerateDevices();
    setDevices(devs.filter((d) => d.kind === "audioinput"));
    if (stream && !deviceId) setDeviceId(devs.find((d) => d.kind === "audioinput")?.deviceId || "");
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: deviceId ? { deviceId: { exact: deviceId } } : true });
      listMics(stream);
      const ctx = new AudioContext();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const meter = () => {
        analyser.getByteTimeDomainData(data);
        let m = 0;
        for (const x of data) m = Math.max(m, Math.abs(x - 128) / 128);
        setLevel(m);
        meterRef.current = requestAnimationFrame(meter);
      };
      meter();
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        cancelAnimationFrame(meterRef.current);
        stream.getTracks().forEach((t) => t.stop());
        await ctx.close();
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        await loadBlob(blob, true);
      };
      mrRef.current = mr;
      mr.start();
      setSecs(0); setState("recording");
      timerRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch {
      toast.error("No microphone detected — allow mic access and try again.");
    }
  };

  const stopRec = () => {
    clearInterval(timerRef.current);
    if (mrRef.current?.state === "recording") mrRef.current.stop();
  };

  const loadBlob = async (blob, trim) => {
    try {
      const ctx = new AudioContext();
      let buf = await ctx.decodeAudioData(await blob.arrayBuffer());
      if (trim) buf = trimSilence(ctx, buf);
      await ctx.close();
      bufRef.current = buf;
      setDuration(buf.duration);
      setPeaks(peaksFor(buf));
      setMarkers((m) => ({
        talk_over_point: m.talk_over_point || BED_LEAD * 0.5,
        intro_start: m.intro_start || BED_LEAD,
        vocal_start: m.vocal_start || BED_LEAD + buf.duration + BED_TAIL * 0.6,
        hook_point: m.hook_point || BED_LEAD + buf.duration * 0.5,
        outro_start: m.outro_start || 1,
        end: BED_LEAD + buf.duration + BED_TAIL,
      }));
      setState("ready");
    } catch {
      toast.error("Couldn't decode that audio file");
      setState("idle");
    }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (f) { setTitle((t) => t || f.name.replace(/\.[^.]+$/, "")); await loadBlob(f, false); }
    e.target.value = "";
  };

  const loadExisting = async () => {
    if (!vt?.vt_id) return;
    setLoadingExisting(true);
    try {
      const r = await fetch(`${APP_ORIGIN}/api/production/voicetracks/${vt.vt_id}/audio`, { credentials: "include" });
      if (!r.ok) throw new Error();
      await loadBlob(await r.blob(), false);
      toast.success("Recording loaded into the editor");
    } catch { toast.error("No saved recording on this voice track yet"); }
    setLoadingExisting(false);
  };

  // ── Auto-duck preview: real song beds (or synth fallback) + voice ──
  const stopPreview = () => {
    previewCtxRef.current?.close().catch(() => {});
    previewCtxRef.current = null;
    setPreviewing(false);
  };

  const bedBuf = async (id) => {
    if (bedBufsRef.current[id]) return bedBufsRef.current[id];
    const r = await fetch(`${APP_ORIGIN}/api/production/voicetrack/beds/${id}/audio`, { credentials: "include" });
    if (!r.ok) throw new Error("bed fetch failed");
    const dctx = new AudioContext();
    const buf = await dctx.decodeAudioData(await r.arrayBuffer());
    await dctx.close();
    bedBufsRef.current[id] = buf;
    return buf;
  };

  const onBedUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBedUp(true);
    try {
      const fd = new FormData();
      fd.append("audio", f);
      fd.append("title", f.name.replace(/\.[^.]+$/, ""));
      const r = await api.post("/production/voicetrack/beds/upload", fd);
      const bed = r.data.bed;
      setBeds((b) => [bed, ...b]);
      setBedSel((s) => (s.lead === "synth" ? { ...s, lead: bed.id } : { ...s, tail: bed.id }));
      toast.success(`"${bed.title}" added and selected as a song bed`);
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Upload failed"); }
    setBedUp(false);
  };

  const preview = async () => {
    if (!bufRef.current) return;
    stopPreview();
    let leadBuf = null, tailBuf = null;
    try {
      if (bedSel.lead !== "synth") leadBuf = await bedBuf(bedSel.lead);
      if (bedSel.tail !== "synth") tailBuf = await bedBuf(bedSel.tail);
    } catch {
      toast.error("Couldn't load a song bed — using the synth bed instead");
    }
    const ctx = new AudioContext();
    previewCtxRef.current = ctx;
    const bedGain = ctx.createGain();
    bedGain.gain.value = duck.music;
    bedGain.connect(ctx.destination);
    const t0 = ctx.currentTime;
    const vStart = t0 + Math.min(markers.talk_over_point ?? BED_LEAD * 0.5, BED_LEAD);
    const vEnd = vStart + bufRef.current.duration;
    if (leadBuf) {
      const s = ctx.createBufferSource();
      s.buffer = leadBuf;
      s.connect(bedGain);
      const seg = Math.min(leadBuf.duration, BED_LEAD + Math.min(2.5, bufRef.current.duration));
      s.start(t0, Math.max(0, leadBuf.duration - seg), seg);
    }
    if (tailBuf) {
      const s = ctx.createBufferSource();
      s.buffer = tailBuf;
      s.connect(bedGain);
      const start = Math.max(t0, vEnd - Math.min(2.5, bufRef.current.duration * 0.4));
      s.start(start, 0, Math.min(tailBuf.duration, (vEnd - start) + BED_TAIL + 1.5));
    }
    if (!leadBuf || !tailBuf) {
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass"; lp.frequency.value = 900;
      lp.connect(bedGain);
      [130.81, 164.81, 196.0].forEach((f, i) => {
        const o = ctx.createOscillator();
        o.type = i ? "triangle" : "sawtooth";
        o.frequency.value = f;
        const g = ctx.createGain(); g.gain.value = 0.055;
        o.connect(g).connect(lp);
        if (!leadBuf && !tailBuf) { o.start(t0); o.stop(t0 + timeline); }
        else if (!leadBuf) { o.start(t0); o.stop(vStart + 1.5); }
        else { o.start(Math.max(t0, vEnd - 1.5)); o.stop(t0 + timeline); }
      });
    }
    const vGain = ctx.createGain(); vGain.gain.value = duck.voice;
    const src = ctx.createBufferSource();
    src.buffer = bufRef.current;
    src.connect(vGain).connect(ctx.destination);
    src.start(vStart);
    if (duck.on) {
      const duckLevel = duck.music * Math.pow(10, duck.amount / 20);
      bedGain.gain.setValueAtTime(duck.music, Math.max(t0, vStart - 0.01));
      bedGain.gain.linearRampToValueAtTime(duckLevel, vStart + duck.attack / 1000);
      bedGain.gain.setValueAtTime(duckLevel, vEnd);
      bedGain.gain.linearRampToValueAtTime(duck.music, vEnd + duck.release / 1000);
    }
    src.onended = () => setTimeout(() => { if (previewCtxRef.current === ctx) stopPreview(); }, BED_TAIL * 1000);
    setPreviewing(true);
  };

  // ── Draggable markers ──
  const onMarkerDown = (key) => (e) => { e.preventDefault(); dragRef.current = key; };
  useEffect(() => {
    const move = (e) => {
      if (!dragRef.current || !waveRef.current) return;
      const r = waveRef.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
      const sec = Math.max(0, Math.min(timeline, (x / r.width) * timeline));
      setMarkers((m) => ({ ...m, [dragRef.current]: Math.round(sec * 10) / 10 }));
    };
    const up = () => {
      if (dragRef.current && vt?.vt_id) {
        setMarkers((m) => {
          api.patch(`/production/voicetracks/${vt.vt_id}`, { markers: m }).catch(() => {});
          return m;
        });
      }
      dragRef.current = null;
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [timeline, vt?.vt_id]);

  const save = async () => {
    if (!bufRef.current) return;
    if (!vt && !title.trim()) { toast.error("Give the voice track a title first"); return; }
    setState("saving");
    try {
      let vtId = vt?.vt_id;
      if (!vtId) {
        const r = await api.post("/production/voicetracks", { title, category: "Music Link" });
        vtId = r.data.vt_id;
      }
      const fd = new FormData();
      fd.append("audio", encodeWav(bufRef.current), "take.wav");
      fd.append("duration", String(Math.round(duration * 10) / 10));
      await api.post(`/production/voicetracks/${vtId}/recording`, fd);
      if (Object.keys(markers).length) await api.patch(`/production/voicetracks/${vtId}`, { markers });
      toast.success("Voice track saved");
      setState("ready");
      onSaved?.(vtId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      setState("ready");
    }
  };

  const vtStartPct = (Math.min(markers.talk_over_point ?? BED_LEAD * 0.5, BED_LEAD) / timeline) * 100;
  const vtWidthPct = (duration / timeline) * 100;

  return (
    <div className="rounded-lg border border-line bg-bg-elevated/60 p-3 space-y-3" data-testid="vt-recorder">
      {/* Transport */}
      <div className="flex flex-wrap items-center gap-2">
        {state !== "recording" ? (
          <button onClick={startRec} data-testid="vt-record-btn" title="Record"
            className="h-9 w-9 rounded-full bg-rose-500 flex items-center justify-center text-white hover:bg-rose-400 transition">
            <Circle className="h-4 w-4 fill-white" /></button>
        ) : (
          <button onClick={stopRec} data-testid="vt-stop-btn" title="Stop"
            className="h-9 w-9 rounded-full bg-rose-600 animate-pulse flex items-center justify-center text-white">
            <Square className="h-4 w-4 fill-white" /></button>
        )}
        <label className="cursor-pointer rounded border border-line p-2 text-ink-muted hover:text-ink-primary" title="Upload audio" data-testid="vt-upload-label">
          <Upload className="h-3.5 w-3.5" />
          <input type="file" accept="audio/*" className="hidden" onChange={onUpload} data-testid="vt-upload-input" />
        </label>
        {state === "ready" && (
          <>
            <button onClick={previewing ? stopPreview : preview} data-testid="vt-preview-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-brand-cyan/50 bg-brand-cyan/10 text-brand-cyan px-3 py-1.5 text-xs font-bold">
              {previewing ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />} {previewing ? "Stop" : "Preview with duck"}
            </button>
            <button onClick={() => { bufRef.current = null; setPeaks(null); setDuration(0); setState("idle"); stopPreview(); }}
              data-testid="vt-discard-btn" className="rounded border border-line p-2 text-ink-muted hover:text-rose-300" title="Discard take">
              <RotateCcw className="h-3.5 w-3.5" /></button>
          </>
        )}
        <span className="font-mono text-sm text-ink-secondary" data-testid="vt-timer">
          {state === "recording" ? `0:${String(secs).padStart(2, "0")}` : duration ? `${duration.toFixed(1)}s` : "00:00.0"}
        </span>
        <div className="h-1.5 w-20 rounded-full bg-bg-surface overflow-hidden" title="Input level">
          <div className="h-full rounded-full transition-[width] duration-75"
            style={{ width: `${Math.min(100, level * 130)}%`, background: level > 0.85 ? "#ef4444" : "#22c55e" }} data-testid="vt-level-meter" />
        </div>
        <select value={deviceId} onChange={(e) => setDeviceId(e.target.value)} onFocus={() => listMics()}
          data-testid="vt-mic-select" className="ml-auto max-w-[160px] rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs">
          <option value="">Default mic</option>
          {devices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || "Microphone"}</option>)}
        </select>
      </div>

      {/* Waveform timeline + markers */}
      {peaks && (
        <div className="relative select-none" data-testid="vt-wave-editor">
          <div ref={waveRef} className="relative h-20 rounded-md bg-black/30 overflow-hidden">
            <div className="absolute inset-y-0 left-0 flex items-center pl-1 gap-[1.5px]" style={{ width: `${(BED_LEAD / timeline) * 100}%` }}>
              {Array.from({ length: 20 }, (_, i) => <span key={i} className="w-1 rounded-sm bg-indigo-400/60" style={{ height: `${25 + (i * 13) % 45}%` }} />)}
            </div>
            <div className="absolute inset-y-0 flex items-center gap-[1.5px] px-0.5" style={{ left: `${vtStartPct}%`, width: `${vtWidthPct}%` }}>
              {peaks.map((p, i) => <span key={i} className="flex-1 rounded-sm bg-brand-amber" style={{ height: `${p * 88}%` }} />)}
            </div>
            <div className="absolute inset-y-0 right-0 flex items-center justify-end pr-1 gap-[1.5px]" style={{ width: `${(BED_TAIL / timeline) * 100}%` }}>
              {Array.from({ length: 20 }, (_, i) => <span key={i} className="w-1 rounded-sm bg-sky-400/60" style={{ height: `${25 + (i * 17) % 50}%` }} />)}
            </div>
            {MARKER_DEFS.map(([key, label]) => (
              markers[key] !== undefined && (
                <div key={key} className="absolute inset-y-0 cursor-ew-resize group" data-testid={`vt-marker-${key}`}
                  style={{ left: `calc(${(Math.min(markers[key], timeline) / timeline) * 100}% - 5px)`, width: 10 }}
                  onPointerDown={onMarkerDown(key)}>
                  <div className="mx-auto h-full w-px bg-emerald-400/80 group-hover:bg-emerald-300" />
                  <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 h-2 w-2 rounded-full bg-emerald-400 group-hover:scale-125 transition-transform" />
                </div>
              )
            ))}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            {MARKER_DEFS.map(([key, label]) => (
              <span key={key} className="font-mono text-[8px] uppercase tracking-wider text-ink-muted">
                <span className="text-emerald-400">▎</span>{label} {markers[key] !== undefined ? `${Number(markers[key]).toFixed(1)}s` : "—"}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Song bed pickers */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] text-ink-secondary" data-testid="vt-bed-controls">
        <span className="font-bold inline-flex items-center gap-1"><Disc3 className="h-3 w-3" /> Song beds</span>
        {[["lead", "Prev song (outro)"], ["tail", "Next song (intro)"]].map(([slot, label]) => (
          <label key={slot} className="flex items-center gap-1.5">{label}
            <select value={bedSel[slot]} onChange={(e) => setBedSel({ ...bedSel, [slot]: e.target.value })}
              data-testid={`vt-bed-${slot}`} className="max-w-[150px] rounded-md border border-line bg-bg-elevated px-1.5 py-1 text-[10px]">
              <option value="synth">Synth demo bed</option>
              {beds.some((b) => b.source === "upload") && (
                <optgroup label="Uploaded songs">
                  {beds.filter((b) => b.source === "upload").map((b) => <option key={b.id} value={b.id}>{b.title}{b.duration ? ` (${Math.round(b.duration)}s)` : ""}</option>)}
                </optgroup>
              )}
              {beds.some((b) => b.source === "library") && (
                <optgroup label="Imaging library">
                  {beds.filter((b) => b.source === "library").map((b) => (
                    <option key={b.id} value={b.id}>{b.title}{b.duration ? ` (${Math.round(b.duration)}s)` : ""}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
        ))}
        <label className="cursor-pointer inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[10px] font-bold text-ink-muted hover:text-brand-amber hover:border-brand-amber/40 transition"
          title="Upload the actual song file (MP3/WAV)" data-testid="vt-bed-upload-label">
          {bedUp ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload song
          <input type="file" accept="audio/*" className="hidden" onChange={onBedUpload} data-testid="vt-bed-upload-input" />
        </label>
      </div>

      {/* Duck controls */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-ink-secondary" data-testid="vt-duck-controls">
        <label className="flex items-center gap-1.5 font-bold">Auto Duck
          <input type="checkbox" checked={duck.on} onChange={(e) => setDuck({ ...duck, on: e.target.checked })} data-testid="vt-duck-toggle" />
        </label>
        <label className="flex items-center gap-1.5">Duck {duck.amount}dB
          <input type="range" min={-24} max={-3} value={duck.amount} onChange={(e) => setDuck({ ...duck, amount: Number(e.target.value) })}
            className="w-20 accent-amber-500" data-testid="vt-duck-amount" />
        </label>
        <label className="flex items-center gap-1.5">Attack {duck.attack}ms
          <input type="range" min={10} max={500} step={10} value={duck.attack} onChange={(e) => setDuck({ ...duck, attack: Number(e.target.value) })}
            className="w-16 accent-amber-500" data-testid="vt-duck-attack" />
        </label>
        <label className="flex items-center gap-1.5">Release {duck.release}ms
          <input type="range" min={50} max={2000} step={50} value={duck.release} onChange={(e) => setDuck({ ...duck, release: Number(e.target.value) })}
            className="w-16 accent-amber-500" data-testid="vt-duck-release" />
        </label>
        <label className="flex items-center gap-1.5"><Volume2 className="h-3 w-3" /> Music {Math.round(duck.music * 100)}%
          <input type="range" min={0} max={1} step={0.05} value={duck.music} onChange={(e) => setDuck({ ...duck, music: Number(e.target.value) })}
            className="w-16 accent-amber-500" data-testid="vt-duck-music" />
        </label>
      </div>

      {/* Save row */}
      <div className="flex flex-wrap items-center gap-2">
        {!vt && (
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Voice track title"
            data-testid="vt-quick-title" maxLength={140}
            className="flex-1 min-w-[160px] rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs" />
        )}
        {vt && <span className="text-[11px] text-ink-muted">Editing: <b className="text-ink-primary">{vt.title}</b></span>}
        {vt?.has_audio && state === "idle" && (
          <button onClick={loadExisting} disabled={loadingExisting} data-testid="vt-load-existing"
            className="rounded-md border border-line px-3 py-1.5 text-[11px] font-bold text-ink-secondary hover:text-ink-primary">
            {loadingExisting ? "Loading…" : "Load saved recording"}
          </button>
        )}
        <button onClick={save} disabled={state !== "ready"} data-testid="vt-save-btn"
          className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-bold text-black disabled:opacity-40">
          {state === "saving" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save Voice Track
        </button>
      </div>

      {/* AI generation routes — existing engines, no parallel paths */}
      <div className="flex flex-wrap gap-1.5 border-t border-line/50 pt-2.5">
        {[["AI Generate Voice", "/dashboard/artist-drops"], ["Speech-to-Speech", "/dashboard/vocalist-studio"], ["Text-to-Speech", "/dashboard/imaging"]].map(([label, to]) => (
          <button key={label} onClick={() => navigate(to)} data-testid={`vt-src-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="rounded-md border border-line px-3 py-1.5 text-[10px] font-semibold text-ink-muted hover:text-brand-amber hover:border-brand-amber/40 transition">
            {label} ↗
          </button>
        ))}
        <span className="text-[9px] text-ink-muted self-center">Generate there, then Upload the file here.</span>
      </div>
    </div>
  );
}
